import { getControlUnitLogsOptions, getSystemLogsOptions as getCuSystemLogsOptions } from '@/api/cuSystemAnalyzerApi'
import { getCitysenseCarLogsOptions, getSystemLogsOptions } from '@/api/systemAnalyzerApi'
import { Client } from '@/api/systemAnalyzerApi/client'
import ErrorState from '@/components/Common/ErrorState'
import { DIAGNOSTICS_LOGS_REFETCH_INTERVAL_MS } from '@/constants'
import { extendKey } from '@/utils/queryKey'
import { Button, Card, Code, Group, LoadingOverlay, ScrollArea, Tabs, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { FaSync } from 'react-icons/fa'
import { DiagnosticsSystem } from './types'
import { useDiagnosticsQuery } from './useDiagnosticsQuery'
import { useDiagnosticsTarget } from './useDiagnosticsTarget'
import { isLogText } from './validation'

type LogsTab = 'citysense-car-logs' | 'system-logs' | 'control-unit-logs' | 'cu-system-logs'

const LOGS_SYSTEMS: Record<LogsTab, DiagnosticsSystem> = {
  'citysense-car-logs': 'cs',
  'system-logs': 'cs',
  'control-unit-logs': 'cu',
  'cu-system-logs': 'cu',
}

/**
 * The car server and control unit clients share the same generated client shape,
 * so a single client prop serves either system analyzer
 */
const getLogsOptions = (value: LogsTab, client: Client) => {
  switch (value) {
    case 'citysense-car-logs':
      return getCitysenseCarLogsOptions({ client, parseAs: 'text' })
    case 'system-logs':
      return getSystemLogsOptions({ client, parseAs: 'text' })
    case 'control-unit-logs':
      return getControlUnitLogsOptions({ client, parseAs: 'text' })
    case 'cu-system-logs':
      return getCuSystemLogsOptions({ client, parseAs: 'text' })
  }
}

const LogsPanel = ({
  value,
  title,
  client,
  source,
}: {
  value: LogsTab
  title: string
  client: Client
  source: string
}) => {
  const { t } = useTranslation(['diagnostics', 'common'])
  const target = useDiagnosticsTarget(LOGS_SYSTEMS[value], source)

  const { data, isLoading, isFetching, isError, refetch } = useDiagnosticsQuery({
    ...extendKey(getLogsOptions(value, client), source, value),
    refetchInterval: DIAGNOSTICS_LOGS_REFETCH_INTERVAL_MS,
  })

  const logs = isLogText(data) ? data : undefined
  const hasInvalidResponse = !isError && data !== undefined && logs === undefined

  return (
    <Tabs.Panel value={value}>
      <Card shadow="sm" p="lg" radius="md" withBorder mt="lg" pos="relative">
        <LoadingOverlay visible={isLoading} />
        <Group justify="space-between" align="center" mb="md">
          <Text fw={500}>{title}</Text>
          <Button
            variant="light"
            size="xs"
            leftSection={<FaSync size={12} />}
            onClick={() => refetch()}
            loading={isFetching}
          >
            {t('refresh')}
          </Button>
        </Group>

        {isError && (
          <ErrorState
            title={t('logsFetchFailed')}
            message={t('logsFetchFailedMessage', { source: target })}
            showBackButton={false}
            width="100%"
          />
        )}

        {hasInvalidResponse && (
          <ErrorState
            title={t('unexpectedResponse')}
            message={t('logsUnexpectedResponseMessage', { source: target })}
            showBackButton={false}
            width="100%"
          />
        )}

        {!isError &&
          !hasInvalidResponse &&
          (logs ? (
            <ScrollArea h={600} type="auto">
              <Code block style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {logs}
              </Code>
            </ScrollArea>
          ) : (
            !isLoading && <Text size="sm">{t('noLogs')}</Text>
          ))}
      </Card>
    </Tabs.Panel>
  )
}

export default LogsPanel
